import React from 'react';
import { Modal } from 'react-bootstrap';

import { UploadStatuses, UploadStatusWidget } from '../../utils/import';
import { UploadTaskState } from '../beneficiaries';
import { getAsyncTask } from '../../apiClient';
import { AsyncTask, AsyncTasksTypes } from '../../utils/asyncTasks';

export interface DryRunDialogProps {
    title: string;
    asyncTask?: AsyncTask;
    taskStateError: string;

    onCancel: () => void;
    onConfirm: (dryRunAsyncTaskId: string, autoComplete?: boolean) => void;
    downloadFile?: (type: string, id: string) => void;
}

export interface DryRunDialogState {
    taskState: UploadTaskState;
    refreshHandle?: number;
    asyncTask?: AsyncTask;
    autoComplete: boolean;
    showAutoCompleteModal: boolean;
}

export default class DryRunDialog extends React.Component<DryRunDialogProps, DryRunDialogState> {
    constructor(props: DryRunDialogProps) {
        super(props);

        this.state = {
            taskState: new UploadTaskState(UploadStatuses.pending),
            asyncTask: null,
            autoComplete: false,
            showAutoCompleteModal: false,
        };
    }

    componentDidMount() {
        this.setState({
            refreshHandle: window.setInterval(this.loadTaskState, 2000),
        });
    }

    componentWillUnmount() {
        if (this.state.refreshHandle) {
            clearInterval(this.state.refreshHandle);
        }
    }

    private loadTaskState = async () => {
        if (this.props.taskStateError) {
            this.setState({
                taskState: new UploadTaskState(UploadStatuses.finished, null, this.props.taskStateError),
            });
        } else if (this.props.asyncTask) {
            const response = await getAsyncTask(this.props.asyncTask.type, this.props.asyncTask.id);
            const taskStatus =
                response.task.finishedAt && response.task.children && response.task.children.length > 0
                    ? UploadStatuses.finished
                    : UploadStatuses.pending;

            const taskState = new UploadTaskState(taskStatus, response.task);
            this.setState({
                taskState,
                asyncTask: response.task,
            });
            if (taskStatus !== UploadStatuses.pending) {
                clearInterval(this.state.refreshHandle);
                this.setState({
                    refreshHandle: undefined,
                });
            } else {
                this.setState({ taskState });
            }
        }
    };

    private handleAutoCompleteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            this.setState({ showAutoCompleteModal: true });
        } else {
            this.setState({ autoComplete: false });
        }
    };

    private handleAutoCompleteConfirm = () => {
        this.setState({
            autoComplete: true,
            showAutoCompleteModal: false,
        });
    };

    private handleAutoCompleteCancel = () => {
        this.setState({
            autoComplete: false,
            showAutoCompleteModal: false,
        });
    };

    render() {
        const { title, onCancel, onConfirm, downloadFile } = this.props;
        const { taskState, autoComplete, showAutoCompleteModal } = this.state;
        const errors = (taskState.task && taskState.task.errors) || [];
        const warnings = (taskState.task && taskState.task.warnings) || [];
        const isBeneficiaryMappingTask = this.state.asyncTask?.type === AsyncTasksTypes.IMPORT_BENEFICIARIES_MAPPING;

        return (
            <div>
                <Modal backdrop="static" centered keyboard={false} onHide={onCancel} show>
                    <Modal.Header>
                        <Modal.Title>{title}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <UploadStatusWidget
                            downloadFile={downloadFile}
                            importError={taskState.error}
                            importStatus={taskState.status}
                            importTask={taskState.task}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        {!!(errors.length || warnings.length) && (
                            <p className="modal-label">The link above provided the validation results.</p>
                        )}
                        {isBeneficiaryMappingTask && (
                            <div
                                style={{ marginBottom: 10, display: 'flex', justifyContent: 'flex-end', width: '100%' }}
                            >
                                <label style={{ display: 'flex', alignItems: 'center' }}>
                                    <input
                                        checked={autoComplete}
                                        onChange={this.handleAutoCompleteChange}
                                        style={{ marginRight: 8 }}
                                        type="checkbox"
                                    />
                                    Auto-finish after this file
                                </label>
                            </div>
                        )}
                        <button className="wfp-btn" onClick={onCancel} style={{ marginRight: 10 }} type="button">
                            Cancel
                        </button>
                        <button
                            className="wfp-btn--primary"
                            disabled={this.shouldRunButtonBeDisabled(taskState)}
                            onClick={() => onConfirm(taskState.task.id, autoComplete)}
                            type="button"
                        >
                            Run import
                        </button>
                    </Modal.Footer>
                </Modal>

                {showAutoCompleteModal && (
                    <div>
                        <div
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                backdropFilter: 'blur(2px)',
                                WebkitBackdropFilter: 'blur(2px)',
                                zIndex: 1055,
                            }}
                        />
                        <Modal.Dialog
                            style={{
                                zIndex: 1060,
                                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                            }}
                        >
                            <Modal.Header>
                                <Modal.Title>Auto-finish Confirmation</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <p>
                                    You have selected auto-finish. This means you won&apos;t be able to upload more
                                    files or cancel the mapping - it will automatically finish the mapping at the end of
                                    processing this file.
                                </p>
                            </Modal.Body>
                            <Modal.Footer>
                                <button
                                    className="wfp-btn"
                                    onClick={this.handleAutoCompleteCancel}
                                    style={{ marginRight: 10 }}
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    className="wfp-btn--primary"
                                    onClick={this.handleAutoCompleteConfirm}
                                    type="button"
                                >
                                    Confirm Auto-finish
                                </button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </div>
                )}
            </div>
        );
    }

    shouldRunButtonBeDisabled(taskState: UploadTaskState): boolean {
        if (taskState.status !== UploadStatuses.finished) {
            return true;
        }
        if (!taskState.task) {
            return true;
        } else if (
            taskState.error ||
            (this.state.asyncTask &&
                this.state.asyncTask.type !== AsyncTasksTypes.IMPORT_BENEFICIARIES &&
                this.state.asyncTask.type !== AsyncTasksTypes.IMPORT_BENEFICIARIES_MAPPING &&
                taskState.task.dryRunErrors.length > 0)
        ) {
            return true;
        } else if (this.state.asyncTask && this.state.asyncTask.errors && this.state.asyncTask.errors.length > 0) {
            return true;
        }
        // For beneficiary mapping tasks, check for validation errors
        else if (
            this.state.asyncTask &&
            this.state.asyncTask.type === AsyncTasksTypes.IMPORT_BENEFICIARIES_MAPPING &&
            taskState.task &&
            taskState.task.additionalInfo &&
            (taskState.task.additionalInfo.failsCount as number) > 0
        ) {
            return true;
        }
        return false;
    }
}
